import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlag } from '../lib/flags.js';
import './Crab.css';

/* ============================================================
   línkè · Clawd 小螃蟹（2026-07-02 深夜定稿）
   铃要的是 clawd-on-desk 里那只官方 Clawd 本蟹 → 直接用项目里的
   原版 GIF（assets/LICENSE：Clawd 为 Anthropic 吉祥物、像素图为
   粉丝二创、非商用个人使用 OK；此站为铃私人密码站，纯个人使用）。
   GIF 在 public/clawd/，画布 302×300、本体在底部（上方是跳跃头程）。
   - 全站角落漂浮（她选的），开关 crab_pet 可收起
   - Sael 生成回复时 → typing；深夜 → sleeping；戳它 → happy/跳/泡泡
   ============================================================ */

/* 聊天页（或其他地方）广播 Sael 的忙闲：saelState('busy'|'idle') */
export function saelState(state) {
  try {
    window.dispatchEvent(new CustomEvent('lingke:sael-state', { detail: state }));
  } catch {}
}

const GIF = {
  idle: '/clawd/clawd-idle.gif',
  typing: '/clawd/clawd-typing.gif',
  thinking: '/clawd/clawd-thinking.gif',
  sleeping: '/clawd/clawd-sleeping.gif',
  happy: '/clawd/clawd-happy.gif',
  jump: '/clawd/clawd-react-double-jump.gif',
  bubble: '/clawd/clawd-bubble.gif',
};

// 戳一下的随机小反应（都是官方动画）
const REACTIONS = ['happy', 'jump', 'bubble'];

const isNight = () => {
  const h = new Date().getHours();
  return h >= 23 || h < 7;
};

/* 拖动位置存这里（每设备各记各的，同主题/强调色待遇） */
const POS_KEY = 'lingke.crab.pos';

function loadPos() {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY));
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return p;
  } catch {}
  return null;
}

function clampPos(x, y, w, h) {
  return {
    x: Math.min(Math.max(-w * 0.3, x), window.innerWidth - w * 0.7),
    y: Math.min(Math.max(0, y), window.innerHeight - h),
  };
}

export default function Crab() {
  const [on] = useFlag('crab_pet');
  const { pathname } = useLocation();
  // 聊天页右下角是发送按钮 → 螃蟹上挪蹲到输入框上方，别压按钮
  const onChat = pathname === '/chat' || pathname === '/';

  const [busy, setBusy] = useState(false);
  const [sleeping, setSleeping] = useState(isNight());
  const [reaction, setReaction] = useState(null); // {type, key}
  const [pos, setPos] = useState(loadPos); // null = 默认右下角
  const lastPokeRef = useRef(0);
  const reactTimerRef = useRef();
  const btnRef = useRef(null);
  const dragRef = useRef(null); // {sx,sy,ox,oy,w,h,moved}

  // Sael 忙闲广播
  useEffect(() => {
    const onState = (e) => setBusy(e.detail === 'busy');
    window.addEventListener('lingke:sael-state', onState);
    return () => window.removeEventListener('lingke:sael-state', onState);
  }, []);

  // 深夜睡觉（忙碌或刚被戳过 60s 内不睡）
  useEffect(() => {
    const t = setInterval(() => {
      setSleeping(
        isNight() && !busy && Date.now() - lastPokeRef.current > 60000,
      );
    }, 5000);
    return () => clearInterval(t);
  }, [busy]);

  // 转屏/改窗口大小时把螃蟹拽回屏幕内
  useEffect(() => {
    const onResize = () => {
      setPos((p) => {
        if (!p) return p;
        const r = btnRef.current?.getBoundingClientRect();
        return clampPos(p.x, p.y, r?.width || 140, r?.height || 140);
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // —— 拖动（pointer events，手指/鼠标通吃）——
  // 移动超过 6px 算拖、否则算戳；拖完的位置写 localStorage。
  function onPointerDown(e) {
    const rect = btnRef.current.getBoundingClientRect();
    dragRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      ox: rect.left,
      oy: rect.top,
      w: rect.width,
      h: rect.height,
      moved: false,
    };
    try {
      btnRef.current.setPointerCapture(e.pointerId);
    } catch {}
  }
  function onPointerMove(e) {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    d.moved = true;
    setPos(clampPos(d.ox + dx, d.oy + dy, d.w, d.h));
  }
  function onPointerUp() {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.moved) {
      setPos((p) => {
        try {
          localStorage.setItem(POS_KEY, JSON.stringify(p));
        } catch {}
        return p;
      });
    } else {
      poke();
    }
  }

  function poke() {
    lastPokeRef.current = Date.now();
    setSleeping(false);
    const type = REACTIONS[Math.floor(Math.random() * REACTIONS.length)];
    clearTimeout(reactTimerRef.current);
    setReaction({ type, key: Date.now() });
    reactTimerRef.current = setTimeout(() => setReaction(null), 2400);
  }
  useEffect(() => () => clearTimeout(reactTimerRef.current), []);

  if (!on) return null;

  const src = reaction
    ? GIF[reaction.type]
    : busy
      ? GIF.typing
      : sleeping
        ? GIF.sleeping
        : GIF.idle;

  return (
    <button
      ref={btnRef}
      type="button"
      className={'crab-pet' + (onChat && !pos ? ' crab-on-chat' : '')}
      style={
        pos
          ? { left: pos.x + 'px', top: pos.y + 'px', right: 'auto', bottom: 'auto' }
          : undefined
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (dragRef.current = null)}
      aria-label="Clawd 小螃蟹"
      title="戳戳或拖走 Clawd"
    >
      {/* key 让同一 GIF 被重戳时从头播 */}
      <img
        key={reaction?.key || src}
        className="crab-img"
        src={src}
        alt=""
        draggable="false"
      />
    </button>
  );
}
